// components/ColorPaletteGuide.tsx
import {
  Box,
  Grid2 as Grid,
  Paper,
  Typography,
  Tooltip,
  IconButton,
  InputBase,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { ContentCopy, CheckCircleOutline, CancelOutlined } from "@mui/icons-material";
import { useNotify } from "react-admin";
import { useMemo, useState } from "react";
import { BreadcrumbNav } from "../BreadCrumbNav";
import { AlertType } from "@/types";
import { useAppTheme } from "@/hooks/useAppTheme";

const getLuminance = (hex: string) => {
  const [r, g, b] = hex
    .replace("#", "")
    .match(/.{2}/g)
    ?.map((c) => parseInt(c, 16)) ?? [0, 0, 0];
  const [rr, gg, bb] = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rr + 0.7152 * gg + 0.0722 * bb;
};

const getContrastRatio = (hex1: string, hex2: string) => {
  const lum1 = getLuminance(hex1);
  const lum2 = getLuminance(hex2);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05); // ← return raw number
};

const isWCAGAACompliant = (contrastRatio: number) => contrastRatio >= 4.5;

const getAccessibleTextColor = (hex: string) => {
  const parts = hex.replace("#", "").match(/.{2}/g);
  if (!parts || parts.length !== 3) return "#000"; // fallback
  const [r, g, b] = parts.map((c) => parseInt(c, 16));
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#000000" : "#ffffff";
};

const ColorBox = ({ bg }: { bg: string }) => {
  const textColor = getAccessibleTextColor(bg);
  return (
    <Box
      sx={{
        backgroundColor: bg,
        color: textColor,
        height: 100,
        borderRadius: 1,
        mb: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
      }}
    >
      Aa
    </Box>
  );
};

const WhiteContrastBox = ({ hex }: { hex: string }) => (
  <Box
    sx={{
      backgroundColor: "#fff",
      color: hex,
      border: `1px solid ${hex}`,
      height: 100,
      borderRadius: 1,
      mb: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 600,
    }}
  >
    Aa
  </Box>
);

export const ColorPaletteGuide = () => {
  const { theme } = useAppTheme();
  const notify = useNotify();
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState<"textOnColor" | "colorOnWhite">("textOnColor");

  const colorTokens = useMemo(
    () => [
      {
        name: "Primary",
        token: "primary.main",
        hex: theme.palette.primary.main,
        usage: "Main action buttons, highlights",
      },
      {
        name: "Secondary",
        token: "secondary.main",
        hex: theme.palette.secondary.main,
        usage: "Supporting elements, secondary accents",
      },
      {
        name: "Error",
        token: "error.main",
        hex: theme.palette.error.main,
        usage: "Destructive actions, error messages",
      },
      {
        name: "Warning",
        token: "warning.main",
        hex: theme.palette.warning.main,
        usage: "Caution indicators",
      },
      {
        name: "Info",
        token: "info.main",
        hex: theme.palette.info.main,
        usage: "Informational alerts",
      },
      {
        name: "Success",
        token: "success.main",
        hex: theme.palette.success.main,
        usage: "Success states",
      },
      {
        name: "Text Primary",
        token: "text.primary",
        hex: theme.palette.text.primary,
        usage: "Main body text",
      },
      {
        name: "Text Secondary",
        token: "text.secondary",
        hex: theme.palette.text.secondary,
        usage: "Muted text, supporting content",
      },
      {
        name: "Background",
        token: "background.default",
        hex: theme.palette.background.default,
        usage: "App background",
      },
      {
        name: "Paper",
        token: "background.paper",
        hex: theme.palette.background.paper,
        usage: "Cards, panels",
      },
    ],
    [theme]
  );

  const filteredTokens = colorTokens.filter(
    ({ name, token, usage }) =>
      name.toLowerCase().includes(search.toLowerCase()) ||
      token.toLowerCase().includes(search.toLowerCase()) ||
      usage.toLowerCase().includes(search.toLowerCase())
  );

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    notify("Copied to clipboard!", { type: AlertType.SUCCESS });
  };

  return (
    <Box sx={{ p: 4 }}>
      <BreadcrumbNav current="Color Palette" />
      <Box mb={4}>
        <Typography variant="h1" gutterBottom>
          Color Palette
        </Typography>
        <Typography variant="body1" color="text.secondary">
          These are the primary colors in the Aegle design system, including text, background, and status colors.
        </Typography>
      </Box>

      <Box sx={{ mb: 3, display: "flex", gap: 2, alignItems: "center" }}>
        <InputBase
          placeholder="Search tokens or usage..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            px: 2,
            py: 1,
            width: 300,
          }}
        />
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_e, newMode) => newMode && setMode(newMode)}>
          <ToggleButton value="textOnColor">Text on Color</ToggleButton>
          <ToggleButton value="colorOnWhite">Color on White</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={3}>
        {filteredTokens.map(({ name, token, hex, usage }) => {
          const textColorOnColor = getAccessibleTextColor(hex);
          const contrastTextOnColor = getContrastRatio(textColorOnColor, hex);
          const contrastOnWhite = getContrastRatio(hex, "#ffffff");
          const textOnColorPasses = isWCAGAACompliant(contrastTextOnColor);
          const colorTextPassesOnWhite = isWCAGAACompliant(contrastOnWhite);

          return (
            <Grid key={name} size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 2,
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: "50%",
                  }}
                >
                  <Tooltip title="Copy hex">
                    <IconButton onClick={() => handleCopy(hex)} size="small">
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {mode === "textOnColor" ? <ColorBox bg={hex} /> : <WhiteContrastBox hex={hex} />}

                <Typography variant="caption" fontWeight={600} mt={1}>
                  Contrast Checks
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {textColorOnColor.toUpperCase()} on color: {contrastTextOnColor.toFixed(2)}:1
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  {textOnColorPasses ? (
                    <CheckCircleOutline fontSize="small" color="success" />
                  ) : (
                    <CancelOutlined fontSize="small" color="warning" />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {textOnColorPasses ? "Passes WCAG AA" : "Fails WCAG AA"}
                  </Typography>
                </Box>

                <Typography variant="caption" color="text.secondary">
                  Color on white: {contrastOnWhite.toFixed(2)}:1
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  {colorTextPassesOnWhite ? (
                    <CheckCircleOutline fontSize="small" color="success" />
                  ) : (
                    <CancelOutlined fontSize="small" color="warning" />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {colorTextPassesOnWhite ? "Passes WCAG AA" : "Fails WCAG AA"}
                  </Typography>
                </Box>

                <Typography variant="h4">{name}</Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                  {token}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {usage}
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
